export const AD_ROLL_DEFAULTS = Object.freeze({
  isPrerollEnabled: true,
  isMidrollEnabled: true,
  isPostrollEnabled: false,
  postrollAdTagUrl: "",
  midrollPositionPercent: [],
});

export const MIDROLL_MIN_PERCENT = 0;
export const MIDROLL_MAX_PERCENT = 100;

const NUMBER_PATTERN = /^[-+]?(\d+(\.\d+)?|\.\d+)$/;

export const normalizeMidrollPositions = (positions) =>
  Array.from(new Set(positions)).sort((a, b) => a - b);

export const getAdRollSettings = (setting) => {
  const source = setting || {};
  const boolOr = (value, fallback) =>
    typeof value === "boolean" ? value : fallback;

  const rawPositions = Array.isArray(source.midrollPositionPercent)
    ? source.midrollPositionPercent
    : AD_ROLL_DEFAULTS.midrollPositionPercent;

  return {
    isPrerollEnabled: boolOr(source.isPrerollEnabled, AD_ROLL_DEFAULTS.isPrerollEnabled),
    isMidrollEnabled: boolOr(source.isMidrollEnabled, AD_ROLL_DEFAULTS.isMidrollEnabled),
    isPostrollEnabled: boolOr(source.isPostrollEnabled, AD_ROLL_DEFAULTS.isPostrollEnabled),
    postrollAdTagUrl:
      typeof source.postrollAdTagUrl === "string"
        ? source.postrollAdTagUrl
        : AD_ROLL_DEFAULTS.postrollAdTagUrl,
    midrollPositionPercent: rawPositions
      .map(Number)
      .filter((n) => Number.isFinite(n)),
  };
};

export const formatMidrollPositions = (positions) =>
  Array.isArray(positions) && positions.length > 0
    ? positions.map((p) => `${p}%`).join(", ")
    : "";

/**
 * Accepts "25,40,65", "25%, 40%, 65%" or "[25, 40, 65]".
 * Returns { value: number[] } on success or { error: string } on failure.
 */
export const parseMidrollPositions = (input) => {
  let text = String(input ?? "").trim();
  if (text.startsWith("[") && text.endsWith("]")) {
    text = text.slice(1, -1).trim();
  }
  if (text === "") return { value: [] };

  const tokens = text
    .split(",")
    .map((token) => token.trim())
    .filter((token) => token !== "");

  const values = [];
  for (const token of tokens) {
    const numeric = token.replace(/\s*%$/, "");
    if (!NUMBER_PATTERN.test(numeric)) {
      return {
        error: `"${token}" is not a valid percentage. Use comma-separated numbers, e.g. 25, 50, 75.`,
      };
    }
    const value = Number(numeric);
    if (value < MIDROLL_MIN_PERCENT || value > MIDROLL_MAX_PERCENT) {
      return {
        error: `"${token}" is out of range. Each position must be between ${MIDROLL_MIN_PERCENT} and ${MIDROLL_MAX_PERCENT}.`,
      };
    }
    values.push(value);
  }

  return { value: normalizeMidrollPositions(values) };
};

export const validateAdTagUrl = (url) => {
  const value = String(url ?? "").trim();
  if (value === "") return "";
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return "Postroll Ad Tag URL must start with http:// or https://";
    }
    return "";
  } catch (_) {
    return "Please enter a valid URL (e.g. https://pubads.g.doubleclick.net/...)";
  }
};

export const isSameMidrollPositions = (a, b) => {
  const left = normalizeMidrollPositions(a || []);
  const right = normalizeMidrollPositions(b || []);
  return left.length === right.length && left.every((v, i) => v === right[i]);
};
